import React, { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";
import {
  useAppStateStore,
  useDealerProfileStore,
  useLeadStore,
} from "../../../../stores";
import { InputText } from "../index";
import validationProps from "./validationProps";
import { emailRegex, phoneNumberRegex } from "../../../../utilities";

const validatePhoneNumber = (phoneNumberOptional) => {
  const { phonenumber } = validationProps("phonenumber");
  let phoneNumberIsValid = false;
  if (!phoneNumberOptional) {
    phoneNumberIsValid =
      phonenumber?.length === 10 && phoneNumberRegex.test(phonenumber);
  } else if (phoneNumberOptional) {
    if (!phonenumber?.length) {
      phoneNumberIsValid = true;
    } else {
      phoneNumberIsValid = phoneNumberRegex.test(phonenumber);
    }
  }
  useAppStateStore.setValidity("aboutYou", {
    phoneNumber: phoneNumberIsValid,
  });
};

const validateEmail = () => {
  const { email } = validationProps("email");
  useAppStateStore.setValidity("aboutYou", { email: emailRegex.test(email) });
};

const handleOnPhoneNumberChange = (event) => {
  let phone = event.target.value;
  phone = phone.replace(/[^0-9.]/g, "");
  if (phone.length === 11) return;
  useLeadStore.set({
    consumer: {
      ...useLeadStore.get("consumer"),
      phonenumber: phone,
    },
  });
};

const handleOnEmailChange = (e) => {
  useLeadStore.set({
    consumer: {
      ...useLeadStore.get("consumer"),
      email: e.target.value,
    },
  });
};

const maskedPhoneNumber = (phone) => {
  if (!phone) return;
  if (!phone.slice(3, 6)) {
    return phone.slice(0, 3);
  }
  if (!phone.slice(6, 10)) {
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}`;
  }
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
};

const errorText = (item, message) => {
  const errorSpecificity = !item ? "your" : "a valid";
  return message.replace("your", errorSpecificity);
};

const InputPhoneNumber = ({ className }) => {
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);
  const inputRef = useRef(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const dealerProfile = useStore(useDealerProfileStore);
  const { phoneNumberOptional } = dealerProfile;
  const required = !phoneNumberOptional;

  const setSelectionStartAndEnd = (event) => {
    const input = event.target;
    setSelection({
      start: input.selectionStart,
      end: input.selectionEnd,
    });
  };

  const handleClick = (event) => setSelectionStartAndEnd(event);

  const handleChange = (event) => {
    setSelectionStartAndEnd(event);
    handleOnPhoneNumberChange(event);
  };

  useEffect(() => {
    const input = inputRef.current;
    const valueLength = inputRef.current.value.length;
    let selectionStart = selection.start;
    let selectionEnd = selection.end;

    if (
      (valueLength === 7 && selectionEnd === 4) ||
      (valueLength === 11 && selectionEnd === 10)
    ) {
      selectionStart = valueLength;
      selectionEnd = valueLength;
      setSelection({ start: valueLength, end: valueLength });
    }

    setTimeout(() => {
      input.setSelectionRange(selectionStart, selectionEnd);
    }, 0);

    validatePhoneNumber(phoneNumberOptional);
  }, [lead.consumer.phonenumber]);

  return (
    <InputText
      id="phone-number"
      dataTestId="phone-number"
      label="Phone Number"
      type="tel"
      inputMode="numeric"
      autocomplete="tel-national"
      errorText={errorText(
        lead.consumer.phonenumber,
        "Please enter your phone number",
      )}
      onClick={handleClick}
      onChange={handleChange}
      value={maskedPhoneNumber(lead.consumer.phonenumber) || ""}
      hasError={
        appState.formSubmitted && !appState.isValid.aboutYou.phoneNumber
      }
      className={className}
      ref={inputRef}
      required={required}
      displayRequirementIndicator={true}
    />
  );
};

const InputEmail = ({ className }) => {
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);

  useEffect(() => {
    validateEmail();
  }, [lead.consumer.email]);

  return (
    <InputText
      id="email"
      dataTestId="email"
      type="email"
      label="Email"
      errorText={errorText(
        lead.consumer.email,
        "Please enter your email address",
      )}
      onChange={handleOnEmailChange}
      value={lead.consumer.email}
      hasError={appState.formSubmitted && !appState.isValid.aboutYou.email}
      className={className}
      displayRequirementIndicator={true}
    />
  );
};

const InfoContact = () => {
  return (
    <div className="row">
      <InputPhoneNumber />
      <InputEmail />
    </div>
  );
};

export default InfoContact;
export { InputPhoneNumber, InputEmail };
